/**
 * Sentry Configuration and Initialization
 * 
 * Integrates Sentry error monitoring with existing error reporting system
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry with React integration
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production';
  const release = import.meta.env.VITE_SENTRY_RELEASE || 'unknown';

  if (!dsn) {
    console.warn('Sentry DSN not configured. Error reporting will use local system only.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Performance monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    // Session replay (optional - can be resource intensive)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // React integration
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
      }),
    ],
    
    // Filter out known noise
    beforeSend(event, hint) {
      // Filter out extension errors
      if (event.exception) {
        const error = hint.originalException;
        if (error && error.stack && error.stack.includes('extension://')) {
          return null;
        }
      }
      
      // Filter out known third-party errors
      if (event.message && (
        event.message.includes('Script error') ||
        event.message.includes('Non-Error promise rejection')
      )) {
        return null;
      }
      
      return event;
    },
    
    // Set user context automatically
    initialScope: {
      tags: {
        component: 'booking-platform',
        platform: 'web'
      }
    }
  });

  console.log('Sentry initialized for error monitoring');
}

/**
 * Manually capture an exception to Sentry
 */
export function captureException(error: Error, context?: Record<string, any>): void {
  if (context) {
    Sentry.withScope(scope => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setContext(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Set user context for error tracking
 */
export function setUserContext(user: {
  id?: string;
  email?: string;
  businessId?: string;
}): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    businessId: user.businessId,
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>): void {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Create Sentry Error Boundary for React components
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Export React integration utilities
export { Sentry };

// Import React for router integration
import React from 'react';
